// File: typescript/apps/front-end/src/main/react/list/ssrFallbacks.tsx
//
// One place for "put the server's list back", plus the selectors that identify it.
//
// Rule 32: JS may only improve on the HTML we shipped. Six islands mount over a server-rendered
// crawlable list and, before 2026-08-04, each rendered a retry card on a terminal fetch failure —
// `createRoot()` having already discarded the list. `TagPage` was the sharpest case, because a `/tag/*`
// hub exists ONLY for SEO: the topic hub a crawler rendered was a button. `getSsrSnapshotSubtreeHtml`
// had exactly two consumers repo-wide at the time.
//
// This module is deliberately the single implementation. The three call sites differ in where they
// sit (the shared status-slot factory, `SearchPaginatedList`, and `SeriesDiscussionList`'s hand-rolled
// error branch), and three copies of "read a snapshot and dangerouslySetInnerHTML it" is how the two
// SSRF predicates in this same sweep came to disagree.
//
// Each selector must byte-match the `data-playwright-id` on the JTE partial named beside it. There is
// no compile-time link, and a selector that stops matching fails SILENTLY: it returns null and the
// page degrades to exactly the pre-fix behaviour, which is why the specs assert on the restored
// anchors rather than on the absence of an error.

import React from "react";
import { getSsrSnapshotSubtreeHtml } from "@/main/react/ssrSnapshot";

/** `MainService/src/main/jte/components/crawlable_discussion_list.jte` */
export const CRAWLABLE_DISCUSSION_LIST_SSR_SELECTOR = '[data-playwright-id="crawlable-discussion-list-ssr"]';

/** `MainService/src/main/jte/components/crawlable_group_list.jte` */
export const CRAWLABLE_GROUP_LIST_SSR_SELECTOR = '[data-playwright-id="crawlable-group-list-ssr"]';

/**
 * Identifies the server-rendered list an island mounted over.
 *
 * `componentName` is the island's `data-component`, i.e. the key `captureSsrSnapshot` recorded under.
 */
export type PaginatedListSsrFallback = {
    componentName: string;
    selector: string;
};

/** The discussion list a page's island mounted over. */
export function crawlableDiscussionListFallback(componentName: string): PaginatedListSsrFallback {
    return { componentName, selector: CRAWLABLE_DISCUSSION_LIST_SSR_SELECTOR };
}

/** The group list a page's island mounted over. */
export function crawlableGroupListFallback(componentName: string): PaginatedListSsrFallback {
    return { componentName, selector: CRAWLABLE_GROUP_LIST_SSR_SELECTOR };
}

/**
 * The server's own list, put back verbatim. Renders nothing when there is no snapshot or the selector
 * matches nothing, so a surface with no server-rendered counterpart is unaffected.
 *
 * Safe to render with `dangerouslySetInnerHTML`: the string is our own JTE output, escaped
 * server-side, and `getSsrSnapshotSubtreeHtml` parses it inertly (no scripts run, no images fetch).
 * The restored markup is INERT — links work, interactive controls do not — which is the accepted
 * trade `ssrSnapshot` documents and `DiscussionThoughtsDisplayer` already ships.
 */
export function SsrRestoredList(props: {
    componentName?: string;
    selector?: string;
}): React.ReactElement | null {
    const { componentName, selector } = props;

    const restoredHtml = React.useMemo(
        () => (componentName && selector ? getSsrSnapshotSubtreeHtml(componentName, selector) : null),
        [componentName, selector],
    );

    if (!restoredHtml) {
        return null;
    }

    return (
        <div
            data-playwright-id="paginated-list-ssr-restored"
            // Rule 32's THIRD door — an island's own terminal-fetch branch — and the marker it owes.
            // This path renders no `RecoverableIslandFallback` and logs no error, and the restored
            // list looks EXACTLY like a healthy one, so none of the island-health oracles could see
            // it: a spec asserting on the list's contents passes in full while the island is inert
            // and the page is running on markup the server shipped. That is precisely the "a recovery
            // path without a marker has quietly deleted coverage" case rule 32 names. Asserted by
            // `assertNoIslandFallback` in
            // `typescript/apps/playwright/tests/helpers/islandHealthGuard.mjs`, which every lane runs.
            // The attribute name must byte-match `ISLAND_LIST_RESTORED_SELECTOR` there; there is no
            // compile-time link. Added 2026-08-14.
            data-island-list-restored="true"
            dangerouslySetInnerHTML={{ __html: restoredHtml }}
        />
    );
}

/**
 * A code-split SLOT's Suspense fallback: the server's own subtree, back in the box, while the chunk is
 * pending (FI-046, 2026-09-15).
 *
 * <p>`createRoot()` discards the island's SSR children on mount, so a lazy component inside the island
 * with `fallback={null}` leaves its box EMPTY for as long as its chunk takes, on a page whose HTML had it
 * (rule 32, subtraction through a loading state). This puts the `selector` subtree from the island's
 * snapshot back, inert, and renders nothing on a client-owned arrival (no snapshot), which is exactly
 * today's behaviour there.
 *
 * <p>Deliberately NOT {@link SsrRestoredList}: its `data-island-list-restored` marker means "terminal
 * fetch failure, recovered" to `tests/helpers/islandHealthGuard.mjs`, and a pending chunk is neither a
 * failure nor a recovery. The hook here is a plain `data-playwright-id` so the marker sweep does not
 * read it as an unregistered island marker.
 */
export function SsrSlotFallback(props: {
    componentName: string;
    selector: string;
    playwrightId: string;
}): React.ReactElement | null {
    const { componentName, selector, playwrightId } = props;
    const html = React.useMemo(
        () => getSsrSnapshotSubtreeHtml(componentName, selector),
        [componentName, selector],
    );
    if (!html) {
        return null;
    }
    return <div data-playwright-id={playwrightId} dangerouslySetInnerHTML={{ __html: html }} />;
}
